import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, MapPin } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { href: '/', label: 'Accueil' },
  { href: '/notre-histoire', label: 'Notre histoire' },
  { href: '/commander-en-ligne', label: 'Commander' },
  { href: '/ou-nous-trouver', label: 'Nous trouver' },
  { href: '/blog', label: 'Blog' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems } = useCart();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (href) =>
    href === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(href);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-stone-100'
            : 'bg-white/90 backdrop-blur-sm border-b border-stone-100/80'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20">

            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 shrink-0"
              data-testid="header-logo"
            >
              <img
                src="/images/logo-final.png"
                alt="Villa Marceau"
                className="h-9 sm:h-10 lg:h-12 w-auto"
              />
              <span className="font-heading text-base sm:text-lg lg:text-xl font-semibold text-foreground tracking-wide leading-tight">
                Villa Marceau
              </span>
            </Link>

            {/* Desktop Nav — visible from lg (1024px) */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative px-3 xl:px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                    isActive(link.href)
                      ? 'text-primary bg-primary/8'
                      : 'text-stone-600 hover:text-foreground hover:bg-stone-100/80'
                  }`}
                  data-testid={`nav-link-${link.href.replace('/', '').replace('/', '-') || 'home'}`}
                >
                  {link.label}
                  {isActive(link.href) && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-2">

              {/* Location — icon only on md, icon+text on xl */}
              <Link
                to="/ou-nous-trouver"
                className="hidden md:flex items-center gap-1.5 px-2.5 py-2 rounded-full text-stone-500 hover:text-primary hover:bg-stone-100/80 transition-all duration-200"
                title="Où nous trouver — Tours"
                data-testid="location-btn"
              >
                <MapPin className="w-4 h-4" />
                <span className="hidden xl:inline text-xs font-medium whitespace-nowrap">Tours, France</span>
              </Link>

              {/* Cart */}
              <Link
                to="/panier"
                className="relative flex items-center gap-2 px-3 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-700 transition-all duration-200"
                data-testid="cart-button"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-medium">Panier</span>
                {totalItems > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-primary text-white text-[10px] rounded-full flex items-center justify-center font-bold leading-none">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </Link>

              {/* Hamburger — visible below lg */}
              <button
                className="lg:hidden ml-1 p-2 rounded-full hover:bg-stone-100 transition-colors"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Ouvrir le menu"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile/Tablet drawer — full screen slide-in from right */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 z-[70] w-72 bg-white shadow-2xl lg:hidden flex flex-col"
              data-testid="mobile-menu"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
                <div className="flex items-center gap-2">
                  <img src="/images/logo-final.png" alt="" className="h-8 w-auto" />
                  <span className="font-heading text-base font-semibold">Villa Marceau</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-stone-100 transition-colors"
                  aria-label="Fermer le menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Nav links */}
              <nav className="flex flex-col py-4 px-4 flex-1">
                {navLinks.map((link, idx) => (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.06 }}
                  >
                    <Link
                      to={link.href}
                      className={`flex items-center px-4 py-3.5 rounded-xl text-base font-medium mb-1 transition-colors ${
                        isActive(link.href)
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-stone-100'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${link.href.replace('/', '').replace('/', '-') || 'home'}`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* Drawer footer */}
              <div className="px-6 py-5 border-t border-stone-100">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>26 rue Marceau, 37000 Tours</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
